import { createContext, Dispatch, SetStateAction, useContext } from "react";
import {
  Search,
  GlobalSearchTab,
  SearchEntityType,
  GlobalSearchViewMode,
} from "@/models/search/Search";
import {
  SearchResult,
  LegislationSearchResult,
  WorkspaceSearchResult,
  WorkspaceDocumentSearchResult,
  ChatSearchResult,
} from "@/models/search/SearchResults";
import { SearchPagination } from "@/models/search/SearchPagination";
import { LeveledFiltersConfig } from "@/components/data-table/data-table";
import { DefaultsPlan } from "../legislation-filter/hooks/useLegislationFilterConfig";

export type GlobalSearchContextType = {
  inputValue: string;
  setInputValue: (value: string) => void;
  debouncedSearch: (value: string, tab: GlobalSearchTab) => void;

  // For "All" tab - 4 separate result sets (legacy - keeping for backward compatibility)
  legislationResults?: LegislationSearchResult[];
  workspaceResults?: WorkspaceSearchResult[];
  documentResults?: WorkspaceDocumentSearchResult[];
  chatResults?: ChatSearchResult[];

  // Loading states per entity (for "All" tab)
  isLoadingLegislation: boolean;
  isLoadingWorkspace: boolean;
  isLoadingDocument: boolean;
  isLoadingChat: boolean;

  // HasMore flags per entity (for "All" tab "See more" buttons)
  legislationHasMore: boolean;
  workspaceHasMore: boolean;
  documentHasMore: boolean;
  chatHasMore: boolean;

  // Total records per entity (for displaying actual counts)
  legislationTotalRecords: number;
  documentTotalRecords: number;

  // Modal infinite scroll support
  loadMoreModalResults: () => void;
  isLoadingModalMore: boolean;
  accumulatedLegislationResults: LegislationSearchResult[];
  accumulatedDocumentResults: WorkspaceDocumentSearchResult[];

  // Per-source counts for legislation searches (e.g., {"retsinformation": 10, "arvid": 5})
  sourceCounts?: Record<string, number>;

  // Focused source (to isolate results)
  focusedSource: string | null;
  setFocusedSource: Dispatch<SetStateAction<string | null>>;

  // For individual entity tabs - unified results with infinite scroll
  searchResults: SearchPagination<SearchResult> | undefined;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMoreData: boolean;

  leveledFilters: LeveledFiltersConfig | undefined;
  defaultsPlan: DefaultsPlan;
  sources: string[];
  setSources: (newSources: string[]) => void;
  updateFilter: (filterKey: string, values: string[]) => void;
  loadMoreResults: () => void;
  updateUrl: boolean;
  setUpdateUrl: (updateUrl: boolean) => void;
  searchQuery: Search;
  setSearchQuery: (searchQuery: Search | ((prev: Search) => Search)) => void;
  setIsSearchbarOpen: (isSearchbarOpen: boolean) => void;
  setIsFilterOpen: (isFilterOpen: boolean) => void;
  isSearchbarOpen: boolean;
  isFilterOpen: boolean;

  // New global search modal state
  expandedCategory: SearchEntityType | null;
  setExpandedCategory: (category: SearchEntityType | null) => void;
  globalSearchViewMode: GlobalSearchViewMode;
};

export const GlobalSearchContext = createContext<
  GlobalSearchContextType | undefined
>(undefined);

export const useGlobalSearchContext = () => {
  const context = useContext(GlobalSearchContext);
  if (!context) {
    throw new Error(
      "useGlobalSearchContext must be used within a GlobalSearchProvider"
    );
  }
  return context;
};
